import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  DollarSign, Target, TrendingUp, Users, AlertCircle, Activity, 
  Calendar, HelpCircle, ArrowRight, Sparkles, BarChart3
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import CurrencySelector from '../components/CurrencySelector';
import { useCurrency } from '../context/CurrencyContext';

const SimulatorPage = () => {
  const { format, formatCompact, currentCurrency } = useCurrency();
  
  // State for interactive controls
  const [currentPrice, setCurrentPrice] = useState(100);
  const [targetPrice, setTargetPrice] = useState(120);
  const [volume, setVolume] = useState(1000);
  const [industry, setIndustry] = useState('retail');
  const [marketCondition, setMarketCondition] = useState('normal');

  // Industry-specific elasticity coefficients
  const elasticityMap = {
    retail: -1.2,
    healthcare: -0.5,
    saas: -0.8,
    manufacturing: -0.9,
    services: -1.0,
    trading: -1.5,
    wholesale: -0.7,
    food_beverage: -1.1,
    fitness: -0.9,
    auto_parts: -0.8
  };

  // Market condition multipliers
  const marketMultipliers = {
    high_competition: 0.85,
    normal: 1.0,
    low_competition: 1.15,
    seasonal_high: 1.25
  };

  const elasticity = elasticityMap[industry] || -1.0;
  const marketMultiplier = marketMultipliers[marketCondition] || 1.0;

  // Core calculations
  const priceChange = ((targetPrice - currentPrice) / currentPrice) * 100;
  const demandChange = elasticity * priceChange;
  const newVolume = volume * (1 + demandChange / 100);
  const currentRevenue = currentPrice * volume;
  const projectedRevenue = targetPrice * newVolume * marketMultiplier;
  const revenueImpact = ((projectedRevenue - currentRevenue) / currentRevenue) * 100;

  // Margin calculation
  const baseMargin = 35;
  const marginImpact = (targetPrice - currentPrice) / currentPrice * 10;
  const projectedMargin = Math.max(0, Math.min(100, baseMargin + marginImpact));

  // Competitive score
  const competitiveScore = Math.max(0, Math.min(100, (100 - Math.abs(priceChange) * 0.5) * marketMultiplier));

  // Retention impact
  const retentionImpact = priceChange > 0 
    ? Math.max(-15, -priceChange * 0.5)
    : Math.min(5, Math.abs(priceChange) * 0.3);

  // Risk level calculation
  const getRiskLevel = () => {
    if (Math.abs(priceChange) > 30 || revenueImpact < -10) return 'high';
    if (Math.abs(priceChange) > 15 || revenueImpact < -5) return 'medium';
    return 'low';
  };

  const riskLevel = getRiskLevel();
  const riskColors = {
    low: 'text-green-400 bg-green-500/10 border-green-500/30',
    medium: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30',
    high: 'text-red-400 bg-red-500/10 border-red-500/30'
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <Header />
      
      <main className="pt-40 pb-24 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#2563EB]/10 border border-[#2563EB]/30 rounded-full px-6 py-2 mb-6">
              <Sparkles className="w-4 h-4 text-[#2563EB]" />
              <span className="text-[#2563EB] font-medium text-sm">Interactive Pricing Tool</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#F8FAFC] mb-4">
              Adaptive Pricing Simulator
            </h1>
            <p className="text-xl text-[#94A3B8] max-w-3xl mx-auto mb-6">
              Model pricing scenarios in real-time. See how price changes impact revenue, demand, and margins before you commit.
            </p>
            <div className="flex items-center justify-center gap-3">
              <span className="text-[#94A3B8] text-sm">Currency:</span>
              <CurrencySelector />
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Controls Panel */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-[#F8FAFC] mb-6 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-[#2563EB]" />
                  Simulation Controls
                </h3>
                
                <div className="space-y-6">
                  {/* Industry Selection */}
                  <div>
                    <Label className="text-[#F8FAFC] mb-2 block">Industry</Label>
                    <select 
                      value={industry}
                      onChange={(e) => setIndustry(e.target.value)}
                      className="w-full bg-[#0A0F1C] border border-[#2563EB]/30 rounded-lg px-4 py-3 text-[#F8FAFC] focus:outline-none focus:border-[#2563EB]"
                    >
                      <option value="retail">Retail & E-commerce</option>
                      <option value="wholesale">Wholesale & Distribution</option>
                      <option value="food_beverage">Food & Beverage</option>
                      <option value="auto_parts">Auto Services & Parts</option>
                      <option value="services">Professional Services</option>
                      <option value="fitness">Fitness & Wellness</option>
                      <option value="healthcare">Healthcare</option>
                      <option value="manufacturing">Manufacturing</option>
                      <option value="saas">SaaS / Tech</option>
                    </select>
                  </div>

                  {/* Current Price */}
                  <div>
                    <Label className="text-[#F8FAFC] mb-2 block flex justify-between">
                      <span>Current Price</span>
                      <span className="text-[#2563EB]">{format(currentPrice)}</span>
                    </Label>
                    <input 
                      type="range" 
                      min="10" 
                      max="500" 
                      value={currentPrice}
                      onChange={(e) => setCurrentPrice(Number(e.target.value))}
                      className="w-full accent-[#2563EB]"
                    />
                  </div>

                  {/* Target Price */}
                  <div>
                    <Label className="text-[#F8FAFC] mb-2 block flex justify-between">
                      <span>Target Price</span>
                      <span className="text-[#2563EB]">{format(targetPrice)}</span>
                    </Label>
                    <input 
                      type="range" 
                      min="10" 
                      max="500" 
                      value={targetPrice}
                      onChange={(e) => setTargetPrice(Number(e.target.value))}
                      className="w-full accent-[#2563EB]"
                    />
                  </div>

                  {/* Volume */}
                  <div>
                    <Label className="text-[#F8FAFC] mb-2 block flex justify-between">
                      <span>Monthly Volume</span>
                      <span className="text-[#2563EB]">{volume.toLocaleString()} units</span>
                    </Label>
                    <input 
                      type="range" 
                      min="100" 
                      max="10000" 
                      step="100"
                      value={volume}
                      onChange={(e) => setVolume(Number(e.target.value))}
                      className="w-full accent-[#2563EB]"
                    />
                  </div>

                  {/* Market Condition */}
                  <div>
                    <Label className="text-[#F8FAFC] mb-2 block">Market Condition</Label>
                    <select 
                      value={marketCondition}
                      onChange={(e) => setMarketCondition(e.target.value)}
                      className="w-full bg-[#0A0F1C] border border-[#2563EB]/30 rounded-lg px-4 py-3 text-[#F8FAFC] focus:outline-none focus:border-[#2563EB]"
                    >
                      <option value="high_competition">High Competition</option>
                      <option value="normal">Normal Market</option>
                      <option value="low_competition">Low Competition</option>
                      <option value="seasonal_high">Seasonal High Demand</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Risk Indicator */}
              <div className={`border rounded-xl p-4 ${riskColors[riskLevel]}`}>
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5" />
                  <div>
                    <p className="font-semibold capitalize">{riskLevel} Risk</p>
                    <p className="text-sm opacity-80">
                      {riskLevel === 'low' && 'Safe pricing change'}
                      {riskLevel === 'medium' && 'Monitor customer response'}
                      {riskLevel === 'high' && 'Consider phased rollout'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Results Panel */}
            <div className="lg:col-span-2 space-y-6">
              {/* Key Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-4 h-4 text-[#2563EB]" />
                    <span className="text-sm text-[#94A3B8]">Price Change</span>
                  </div>
                  <p className={`text-2xl font-bold ${priceChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {priceChange >= 0 ? '+' : ''}{priceChange.toFixed(1)}%
                  </p>
                </div>

                <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-[#2563EB]" />
                    <span className="text-sm text-[#94A3B8]">Revenue Impact</span>
                  </div>
                  <p className={`text-2xl font-bold ${revenueImpact >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {revenueImpact >= 0 ? '+' : ''}{revenueImpact.toFixed(1)}%
                  </p>
                </div>

                <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4 text-[#2563EB]" />
                    <span className="text-sm text-[#94A3B8]">Demand Change</span>
                  </div>
                  <p className={`text-2xl font-bold ${demandChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {demandChange >= 0 ? '+' : ''}{demandChange.toFixed(1)}%
                  </p>
                </div>

                <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="w-4 h-4 text-[#2563EB]" />
                    <span className="text-sm text-[#94A3B8]">New Margin</span>
                  </div>
                  <p className="text-2xl font-bold text-[#F8FAFC]">
                    {projectedMargin.toFixed(1)}%
                  </p>
                </div>
              </div>

              {/* Revenue Comparison */}
              <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-[#F8FAFC] mb-6">Revenue Comparison</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-[#0A0F1C] rounded-xl p-6 border border-[#2563EB]/10">
                    <p className="text-sm text-[#94A3B8] mb-2">Current Monthly Revenue</p>
                    <p className="text-3xl font-bold text-[#F8FAFC]">
                      {format(currentRevenue)}
                    </p>
                    <p className="text-sm text-[#94A3B8] mt-2">
                      {volume.toLocaleString()} units × {format(currentPrice)}
                    </p>
                  </div>
                  <div className="bg-[#0A0F1C] rounded-xl p-6 border border-green-500/20">
                    <p className="text-sm text-[#94A3B8] mb-2">Projected Monthly Revenue</p>
                    <p className={`text-3xl font-bold ${projectedRevenue >= currentRevenue ? 'text-green-400' : 'text-red-400'}`}>
                      {format(projectedRevenue)}
                    </p>
                    <p className="text-sm text-[#94A3B8] mt-2">
                      {Math.round(newVolume).toLocaleString()} units × {format(targetPrice)}
                    </p>
                  </div>
                </div>

                {/* Annual Projection */}
                <div className="mt-6 pt-6 border-t border-[#2563EB]/20">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#94A3B8]">Annual Revenue Impact</p>
                      <p className={`text-2xl font-bold ${revenueImpact >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {revenueImpact >= 0 ? '+' : ''}{format((projectedRevenue - currentRevenue) * 12)}/year
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-[#94A3B8]">Competitive Score</p>
                      <p className="text-2xl font-bold text-[#F8FAFC]">{competitiveScore.toFixed(0)}/100</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Insights */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-xl p-6">
                  <h4 className="text-lg font-semibold text-[#F8FAFC] mb-4 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-[#2563EB]" />
                    Volume Impact
                  </h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-[#94A3B8]">Current Volume</span>
                      <span className="text-[#F8FAFC]">{volume.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#94A3B8]">Projected Volume</span>
                      <span className={newVolume >= volume ? 'text-green-400' : 'text-red-400'}>
                        {Math.round(newVolume).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#94A3B8]">Customer Retention</span>
                      <span className={retentionImpact >= 0 ? 'text-green-400' : 'text-red-400'}>
                        {retentionImpact >= 0 ? '+' : ''}{retentionImpact.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-xl p-6">
                  <h4 className="text-lg font-semibold text-[#F8FAFC] mb-4 flex items-center gap-2">
                    <HelpCircle className="w-5 h-5 text-[#2563EB]" />
                    Recommendation
                  </h4>
                  <p className="text-[#94A3B8] leading-relaxed">
                    {riskLevel === 'low' && revenueImpact > 0 && (
                      "This price change looks favorable. Consider implementing it across your product line."
                    )}
                    {riskLevel === 'low' && revenueImpact <= 0 && (
                      "Low risk, but revenue may decline. Consider value-adds to justify the change."
                    )}
                    {riskLevel === 'medium' && (
                      "Moderate risk. Consider A/B testing or a phased rollout to gauge customer response."
                    )}
                    {riskLevel === 'high' && (
                      "High risk scenario. We recommend smaller incremental changes or enhanced value proposition."
                    )}
                  </p>
                </div>
              </div>

              {/* CTA */}
              <div className="bg-gradient-to-r from-[#2563EB]/20 to-[#3B82F6]/20 border border-[#2563EB]/30 rounded-2xl p-8 text-center">
                <h3 className="text-2xl font-bold text-[#F8FAFC] mb-3">
                  Ready to optimize your pricing?
                </h3>
                <p className="text-[#94A3B8] mb-6">
                  Get real-time pricing insights powered by price elasticity analysis.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/coming-soon">
                    <Button className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-8 py-3">
                      Get Started Free
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                  <Link to="/pricing">
                    <Button variant="outline" className="border-[#2563EB]/50 text-[#2563EB] hover:bg-[#2563EB]/10 px-8 py-3">
                      View Pricing
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SimulatorPage;
